import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { BuntuEngine } from '../buntu/engine';
import { loadPrefs, savePrefs } from '../buntu/prefs';
import type { BuntuPrefs } from '../buntu/types';
import type { BuntuTenantPolicy } from '../buntu/policy';
import { apiService } from '../services/api';

const INBOX_POLL_MS = 45_000;

/**
 * Mounts the Buntu companion and keeps it aligned with tenant policy + inbox.
 */
export default function BuntuCompanion(): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let prefs: BuntuPrefs = loadPrefs();
    let destroyed = false;
    let inboxTimer = 0;
    const seenIds = new Set<string>();

    const engine = new BuntuEngine({
      root: host,
      atlasUrl: `${process.env.PUBLIC_URL || ''}/buntu/atlas.json`,
      assetUrl: (src) => `${process.env.PUBLIC_URL || ''}/buntu/${src}`,
      prefs,
      onPrefsChange: (next) => {
        prefs = next;
        savePrefs(next);
      },
    });

    const ack = (id: string) => {
      apiService.ackBuntuMessage(id, true).catch(() => undefined);
    };

    const pullInbox = async () => {
      if (destroyed) return;
      try {
        const res = await apiService.getBuntuInbox();
        const items = res?.data?.items || res?.items || [];
        if (!Array.isArray(items) || !items.length) return;
        // Deliver the newest unseen message one at a time.
        const next = items.find((m: { id: string }) => m?.id && !seenIds.has(m.id));
        if (!next) return;
        seenIds.add(next.id);
        engine.showAdminMessage(
          {
            id: next.id,
            body: next.body,
            priority: next.priority || 'info',
            delivery: next.delivery || 'once',
            created_by: next.created_by || null,
            sent_at: next.sent_at || null,
          },
          ack
        );
      } catch {
        /* offline / 401 — companion still works locally */
      }
    };

    // Feeds the Messages panel with full history (including dismissed), which
    // the inbox endpoint intentionally hides.
    const pullHistory = async () => {
      if (destroyed) return;
      try {
        const res = await apiService.listBuntuMessages('sent');
        const items = res?.data?.items || res?.items || [];
        if (!Array.isArray(items)) return;
        engine.mergeHistory(
          items.map((m: any) => ({
            id: m.id,
            body: m.body,
            priority: m.priority || 'info',
            delivery: m.delivery || 'once',
            created_by: m.created_by || null,
            sent_at: m.sent_at || null,
          }))
        );
      } catch {
        /* non-admin users may not list messages — panel still shows pushes */
      }
    };

    const pullPolicy = async () => {
      if (destroyed) return;
      try {
        const res = await apiService.getBuntuRuntime();
        const data = res?.data || res;
        if (!data) return;
        const config = (data.config || {}) as BuntuTenantPolicy;
        // Runtime may flip enabled=false while still returning config.
        if (data.enabled === false && config.enablement) {
          config.enablement = { ...config.enablement, enabled: false };
        }
        const required = !!data.user?.buntu_required;
        engine.applyPolicy(config, required);
      } catch {
        /* keep local defaults */
      }
    };

    const onPolicyUpdated = () => {
      pullPolicy();
    };
    const onInboxUpdated = () => {
      pullInbox();
      pullHistory();
    };

    engine
      .start()
      .then(async () => {
        if (destroyed) return;
        await pullPolicy();
        await pullHistory();
        await pullInbox();
        inboxTimer = window.setInterval(() => {
          pullPolicy();
          pullHistory();
          pullInbox();
        }, INBOX_POLL_MS);
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Buntu companion unavailable:', err);
        }
      });

    window.addEventListener('buntu-policy-updated', onPolicyUpdated);
    window.addEventListener('buntu-inbox-updated', onInboxUpdated);

    return () => {
      destroyed = true;
      window.clearInterval(inboxTimer);
      window.removeEventListener('buntu-policy-updated', onPolicyUpdated);
      window.removeEventListener('buntu-inbox-updated', onInboxUpdated);
      engine.destroy();
    };
  }, []);

  return <div ref={hostRef} aria-hidden="false" />;
}
